/*eslint no-undef: "error"*/
/*eslint-env node*/

/**
 * Include polyfills in polyfills.js
 */
import "what-input";
import { createApp } from "vue";
import { createPinia } from "pinia";
import lazyLoadComponent from "./lazy-load-component.js";
import i18n from "./i18n";
import LazyLoad from "vanilla-lazyload";
import Headroom from "headroom.js/dist/headroom";
import PhotoSwipeLightbox from "photoswipe/lightbox";
import 'photoswipe/style.css';
import { useCookiebot } from './cookiebot.js';
import { vElementVisibility } from '@vueuse/components';

/**
 * Components
 */
import LoadingComponent from "../components/LoadingComponent.vue";
import TheMobileNav from "../components/TheMobileNav.vue";
import TheSearchCollapse from "../components/TheSearchCollapse.vue";
import TheNotificationBar from "../components/TheNotificationBar.vue";
import BaseNavDropdown from "../components/BaseNavDropdown.vue";
import BaseNavCollapse from "../components/BaseNavCollapse.vue";
import BaseDropdownButton from "../components/BaseDropdownButton.vue";
import BaseSlider from "../components/BaseSlider.vue";
import BaseFeedback from "../components/BaseFeedback.vue";
import BaseSliderItem from "../components/BaseSliderItem.vue";
import BaseVideoPlayer from "../components/BaseVideoPlayer.vue";
import BaseModal from "../components/BaseModal.vue";
import BaseCollapse from "../components/BaseCollapse";
import BaseCollapseGroup from "../components/BaseCollapseGroup";
import BaseTooltip from "../components/BaseTooltip";
import BaseTab from "../components/BaseTab";
import BaseTabGroup from "../components/BaseTabGroup";
import BaseCoursesOverview from "../components/BaseCoursesOverview";
import BaseEventsOverview from "../components/BaseEventsOverview";
import BaseTeachersOverview from "../components/BaseTeachersOverview";
import BaseLottie from "../components/BaseLottie";

const cookiebot = useCookiebot()

const BaseStickyHeader = lazyLoadComponent({
  componentFactory: () => import(/* webpackChunkName: "sticky-header" */ "../components/BaseStickyHeader.vue"),
  loading: LoadingComponent,
});

const BaseVideoBlock = lazyLoadComponent({
  componentFactory: () => import(/* webpackChunkName: "video-block" */ "../components/BaseVideoBlock.vue"),
  loading: LoadingComponent,
});

const BaseNewsletterForm = lazyLoadComponent({
  componentFactory: () => import(/* webpackChunkName: "newsletter-form" */ "../components/BaseNewsletterForm.vue"),
  loading: LoadingComponent,
});

const BaseTableLimiter = lazyLoadComponent({
  componentFactory: () => import(/* webpackChunkName: "table-limiter" */ "../components/BaseTableLimiter.vue"),
  loading: LoadingComponent,
});

/**
 * App
 */
const app = createApp({
  components: {
    TheMobileNav,
    TheSearchCollapse,
    TheNotificationBar,
    BaseNavDropdown,
    BaseFeedback,
    BaseNavCollapse,
    BaseDropdownButton,
    BaseVideoBlock,
    BaseSlider,
    BaseSliderItem,
    BaseCoursesOverview,
    BaseEventsOverview,
    BaseTeachersOverview,
    BaseStickyHeader,
    BaseNewsletterForm,
    BaseTableLimiter,
    BaseModal,
    BaseVideoPlayer,
    BaseCollapse,
    BaseCollapseGroup,
    BaseTooltip,
    BaseTab,
    BaseTabGroup,
    BaseLottie,
  },

  directives: {
    ElementVisibility: vElementVisibility,
  },

  data: () => ({
    spritePath: window.spritePath ?? "/dist/img/sprite.svg",
    headroom: null,
    lightbox: null,
    feedbackIsOpen: false,
    heroInView: true,
  }),

  created() {
    this.$i18n.locale = window.locale ?? "nl";
  },

  mounted() {
    window.addEventListener("resize", this.setVwProperty, { passive: true });

    this.$nextTick(() => {
      this.setVwProperty();
      this.setupHeadroom();
    });

    this.lazyLoad = new LazyLoad({
      elements_selector: "[loading=lazy]",
      use_native: true,
      class_loading: "loading",
      class_loaded: "loaded",
    });

    if (!this.lightbox) {
      this.lightbox = new PhotoSwipeLightbox({
        gallery: '.js-pspw-gallery',
        children: 'a',
        showHideAnimationType: 'none',
        zoomAnimationDuration: false,
        pswpModule: () => import('photoswipe'),
      });

      this.lightbox.on('uiRegister', () => {
        this.lightbox.pswp.ui.registerElement({
          name: 'custom-caption',
          order: 9,
          isButton: false,
          appendTo: 'root',
          html: '',
          onInit: (el) => {
            this.lightbox.pswp.on('change', () => {
              const currSlideElement = this.lightbox.pswp.currSlide.data.element;
              let captionHTML = '';
              if (currSlideElement) {
                const caption = currSlideElement.dataset.pswpCaption;
                if (caption) {
                  captionHTML = caption;
                }
              }
              el.innerHTML = captionHTML || '';
            });
          },
        });
      });
      this.lightbox.init();
    }
  },

  methods: {
    setVwProperty() {
      document.documentElement.style.setProperty("--vw", document.documentElement.clientWidth + "px");
    },

    openCookieSettings() {
      cookiebot.renew();
    },

    setupHeadroom() {
      const headerEl = this.$refs.header;
      if (headerEl) {
        this.headroom  = new Headroom(headerEl, {
          offset: headerEl.scrollHeight,
          tolerance: 10,
          classes: {
            initial: "has-hr",
            pinned: "o-header--pinned",
            unpinned: "o-header--unpinned",
            top: "o-header--top",
            notTop: "o-header--not-top",
            bottom: "o-header--bottom",
            notBottom: "o-header--not-bottom",
            frozen: "o-header--frozen",
          },
          onPin: () => {
            document.body.classList.add("header-pinned");
          },
          onUnpin: () => {
            document.body.classList.remove("header-pinned");
          },
        }).init();
      }
    },

    onHeroVisibility(value) {
      this.heroInView = value
    },

    openModal(ref, referrerTarget) {
      if (this.$refs[ref]) {
        this.$refs[ref].open(referrerTarget);
      }
    },
  },
});

app.use(i18n);
app.use(createPinia());
app.mount("#app");
